import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const CommunityPage = () => {
  // Animation refs
  const heroRef = useScrollAnimation();
  const philosophyRef = useScrollAnimation();
  const impactHeadingRef = useScrollAnimation();
  const impactCard1Ref = useScrollAnimation({ delay: 0 });
  const impactCard2Ref = useScrollAnimation({ delay: 100 });
  const impactCard3Ref = useScrollAnimation({ delay: 150 });
  const impactCard4Ref = useScrollAnimation({ delay: 200 });
  const impactCard5Ref = useScrollAnimation({ delay: 250 });
  const impactCard6Ref = useScrollAnimation({ delay: 300 });
  const standardsImageRef = useScrollAnimation();
  const standardsContentRef = useScrollAnimation({ delay: 100 });
  const visionRef = useScrollAnimation();
  const metricsHeadingRef = useScrollAnimation();
  const metricsCard1Ref = useScrollAnimation({ delay: 0 });
  const metricsCard2Ref = useScrollAnimation({ delay: 100 });
  const metricsCard3Ref = useScrollAnimation({ delay: 200 });
  const ctaRef = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center bg-[#1F2A44] mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}></div>
        </div>
        <div ref={heroRef} className="relative max-w-[1440px] mx-auto px-20 w-full text-center">
          <div className="w-16 h-0.5 bg-[#B89C4B] mx-auto mb-6"></div>
          <h1
            className="text-[56px] text-white leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Community Commitment
          </h1>
          <p
            className="text-[18px] text-white/85 max-w-[640px] mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Responsible ownership that improves housing quality and strengthens neighborhoods
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="bg-white py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={philosophyRef} className="max-w-[800px] mx-auto text-center">
            <p
              className="text-[36px] text-[#1F2A44] leading-relaxed mb-12"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              "We believe quality housing strengthens communities"
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <p
                className="text-[16px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our commitment to community extends beyond financial returns. We believe that improving housing quality, maintaining properties to high standards, and contributing positively to neighborhoods creates sustainable value for all stakeholders.
              </p>
              <p
                className="text-[16px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Every property we acquire undergoes thoughtful improvements that enhance resident experience while supporting the long-term vitality of the surrounding area. We view ourselves as stewards of housing that serves families and strengthens communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Areas Section */}
      <section className="bg-[#F5F5F3] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <h2
            ref={impactHeadingRef}
            className="text-[42px] text-[#1F2A44] text-center mb-16"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Our Focus Areas
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              ref={impactCard1Ref}
              className="bg-white p-11 rounded-lg border-t-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-home-heart-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[21px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Housing Quality
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                We invest in strategic upgrades to units and common areas that improve safety, comfort, and livability. Our renovations focus on durable materials, functional improvements, and creating spaces residents are proud to call home.
              </p>
            </div>

            <div
              ref={impactCard2Ref}
              className="bg-white p-11 rounded-lg border-t-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[21px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Safety & Security
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Property safety is a top priority. We implement proper lighting, secure entry systems, regular maintenance inspections, and responsive management to ensure residents feel safe and protected.
              </p>
            </div>

            <div
              ref={impactCard3Ref}
              className="bg-white p-11 rounded-lg border-t-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-paint-brush-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[21px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Curb Appeal
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Well-maintained exteriors and landscaping contribute to neighborhood quality. We invest in professional landscaping, exterior improvements, and ongoing maintenance that enhances property appearance.
              </p>
            </div>

            <div
              ref={impactCard4Ref}
              className="bg-white p-11 rounded-lg border-t-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-customer-service-2-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[21px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Responsive Management
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Quality property management means responsive service, clear communication, and treating residents with respect. We implement systems that ensure maintenance requests are addressed promptly and professionally.
              </p>
            </div>

            <div
              ref={impactCard5Ref}
              className="bg-white p-11 rounded-lg border-t-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-leaf-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[21px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Environmental Stewardship
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                We incorporate energy-efficient improvements where feasible, implement responsible waste management, and maintain green spaces that benefit both residents and the surrounding community.
              </p>
            </div>

            <div
              ref={impactCard6Ref}
              className="bg-white p-11 rounded-lg border-t-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-community-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[21px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Neighborhood Impact
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our properties are maintained to standards that contribute positively to local neighborhoods. We believe responsible ownership supports property values and community vitality for all residents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Standards Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div ref={standardsImageRef}>
              <img
                src="https://public.readdy.ai/ai/img_res/833d386f9812672d15f4a89142f897bf.jpg"
                alt="Property Standards"
                className="w-full h-[600px] object-cover rounded-lg shadow-lg"
              />
            </div>

            <div ref={standardsContentRef}>
              <h2
                className="text-[38px] text-[#1F2A44] leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Property Standards
              </h2>
              <div className="space-y-5">
                {[
                  'Regular property inspections and preventive maintenance programs',
                  'Professional landscaping and grounds maintenance on consistent schedules',
                  'Prompt response to maintenance requests and resident concerns',
                  'Quality materials and workmanship in all renovation and repair work',
                  'Clean, well-lit common areas and parking facilities',
                  'Proper waste management and recycling programs',
                  'Regular exterior cleaning, painting, and upkeep',
                  'Safe, functional amenities that serve resident needs',
                  'Clear communication channels between management and residents',
                  'Compliance with all housing codes and safety regulations',
                ].map((standard, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 mr-4 rounded-full bg-[#B89C4B]"></div>
                    <p
                      className="text-[16px] text-[#2E2E2E] leading-relaxed"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {standard}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Long-Term Vision Section */}
      <section className="bg-[#1F2A44] py-28">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={visionRef} className="max-w-[760px] mx-auto text-center">
            <h2
              className="text-[40px] text-white leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Building for the Long Term
            </h2>
            <div className="space-y-6">
              <p
                className="text-[16px] text-white/85 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our investment horizon is measured in years, not months. This long-term perspective allows us to make decisions that benefit residents, neighborhoods, and investors over time rather than optimizing for short-term gains.
              </p>
              <p
                className="text-[16px] text-white/85 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                We believe that properties maintained to high standards, with quality improvements and responsive management, create stable housing that serves families and strengthens communities for years to come.
              </p>
              <p
                className="text-[16px] text-white/85 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                This commitment to responsible ownership is not just good for communities—it creates more resilient assets that perform better through market cycles and generate sustainable returns for our investors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics Section */}
      <section className="bg-[#F5F5F3] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={metricsHeadingRef} className="text-center mb-16">
            <h2
              className="text-[42px] text-[#1F2A44] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Our Approach in Action
            </h2>
            <p
              className="text-[17px] text-[#2E2E2E] max-w-[800px] mx-auto"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              We measure success not just by financial returns, but by the quality of housing we provide and the positive impact on the communities we serve
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              ref={metricsCard1Ref}
              className="bg-white p-10 rounded-lg shadow-md text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-home-smile-line text-3xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[20px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Quality Improvements
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Strategic renovations that enhance safety, comfort, and livability for residents
              </p>
            </div>

            <div
              ref={metricsCard2Ref}
              className="bg-white p-10 rounded-lg shadow-md text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-user-heart-line text-3xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[20px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Resident Satisfaction
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Responsive management and clear communication that builds trust and retention
              </p>
            </div>

            <div
              ref={metricsCard3Ref}
              className="bg-white p-10 rounded-lg shadow-md text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-building-line text-3xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[20px] text-[#1F2A44] font-semibold mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Property Performance
              </h3>
              <p
                className="text-[15px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Well-maintained assets that preserve value and contribute to neighborhood stability
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div ref={ctaRef} className="max-w-[1440px] mx-auto px-20 text-center">
          <h2
            className="text-[38px] text-[#1F2A44] mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Learn More About Our Approach
          </h2>
          <p
            className="text-[16px] text-[#2E2E2E] mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Explore how we create value through responsible ownership
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/about"
              className="px-10 py-3.5 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              About Us
            </Link>
            <Link
              to="/contact"
              className="px-10 py-3.5 border-2 border-[#1F2A44] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommunityPage;